import React, { useState } from 'react';
import { openNotification } from '../../utils/notificationAction';
import { Button, DashboardCard } from '../../ui';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import '../dashboard.css';

/**
 * NotificationFeed
 *
 * The dashboard's surface for normal-priority notifications. It was previously the
 * notification half of FollowUpEngine, a mixed card that also rendered client-side
 * follow-up prompts; Block 203b retired the prompt path and this half became a card
 * in its own right.
 *
 * The cards themselves are unchanged from that mixed feed: same markup, same hover,
 * same type-prefix routing, same dismiss.
 *
 * Props:
 *   notifications — normal-priority, undismissed, unread notifications
 *   onDismiss(id) — server-side dismissal (Dashboard's dismissNotification)
 *   onNavigate(view) — top-level view switch (Dashboard's setFilter)
 *   onRead(id) — marks it read on tap (the context's markNotificationAsRead)
 */

// Map notification type → navigation target.
export function notifTarget(n) {
  // Tier 1 #7: the item link from the API comes first (the same one the bell and
  // push use). A portal message card could not be clicked at all before.
  if (n.link) return { link: n.link };
  if (n.view) return { view: n.view };   // item 8: the server's section for the type
  const t = n.type || '';
  if (t.startsWith('leave_')) return { view: 'TeamHR' };
  if (t.startsWith('invoice_')) return { view: 'Invoices' };
  if (t.startsWith('task_')) return { view: 'Tasks' };
  if (t === 'family_chat_message') return { view: 'FamilyChat' };
  return null;
}

// Shape a raw notification into the card model the feed renders.
export function toNotificationCard(n) {
  return {
    id: `notif-${n.id}`,
    _notifId: n.id,
    type: 'notification',
    priority: n.priority,
    label: n.title,
    detail: n.message,
    createdAt: n.createdAt,
    target: notifTarget(n),
    raw: n,
  };
}

// isPersonal: personalFlag() of the active workspace. A section that does not exist
// in that workspace opens Notifications instead (the same rule as the bell).
// Item 8 (2026-10-12): a tap marks it read (onRead) and opens it exactly as the bell does
// (utils/notificationAction openNotification: the item, or its section; never the chat).
export function NotificationCard({ card, onDismiss, onNavigate, onRead, isPersonal = null }) {
  const clickable = !!(card.target && (card.target.link || onNavigate));
  const open = () => {
    if (onRead && card.raw && !card.raw.read) onRead(card._notifId);
    openNotification({ ...(card.raw || {}), link: card.target.link, view: card.raw?.view || card.target.view }, onNavigate, isPersonal);
  };
  return (
    <div
      onClick={clickable ? open : undefined}
      title={card.target ? 'Click to open' : undefined}
      className={`dash-notif${clickable ? ' dash-notif--open' : ''}`}
    >
      <div className="dash-notif__main">
        <span className="dash-notif__title">
          {card.label}
          {clickable && <span className="dash-fu__arrow" aria-hidden="true"> ↗</span>}
        </span>
        {card.detail && <span className="dash-notif__detail">{card.detail}</span>}
      </div>
      <button
        type="button"
        // Guarded as the original handleDismiss was: only dismiss when a handler exists.
        onClick={e => { e.stopPropagation(); if (onDismiss) onDismiss(card._notifId); }}
        title="Dismiss"
        aria-label={`Dismiss ${card.label || 'notification'}`}
        className="dash-x ui-hit"
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

// Fresh-eyes 11 (Carla, 2026-10-12): the tile said "0, nothing needs your attention" while the bell
// said 17 and the page 27. Its header count and its "need attention · unread" line are now the
// server's two numbers (lib/notificationCounts, from the context), the same ones the bell and the
// page show; the cards below are still the recent normal-priority ones.
// 2026-10-21 (Carla): this card is now the ONLY place the needs-attention number appears; the bell and
// the Notifications page show unread. The server resolves notices whose moment has passed before it
// counts (Aidey Backend/lib/notificationResolve.js), so the number can go down without her.
export default function NotificationFeed({ notifications = [], onDismiss, onNavigate, onRead, isPersonal = null }) {
  const [collapsed, setCollapsed] = useState(false);
  const { notificationCounts } = useCurrentUser() || {};
  const counts = notificationCounts && typeof notificationCounts.needsAttention === 'number' ? notificationCounts : null;
  const listed = notifications.length;
  const count = counts ? counts.needsAttention : listed;
  const countLabel = counts
    ? `${count} ${count === 1 ? 'needs' : 'need'} attention`
    : `${count} notification${count !== 1 ? 's' : ''}`;
  const nothingWaiting = counts ? counts.needsAttention === 0 && counts.unread === 0 && listed === 0 : listed === 0;

  return (
    <DashboardCard tint="pastel-mint" title="Notifications" meta={(
      <>
        <span className="dash-count" aria-label={countLabel}>{count}</span>
        {listed > 0 && (
          <button
            type="button"
            className="dash-toggle ui-hit"
            onClick={() => setCollapsed(c => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show notifications' : 'Hide notifications'}
          >
            <span aria-hidden="true">{collapsed ? '▾' : '▴'}</span>
          </button>
        )}
      </>
    )}>

      {counts && !nothingWaiting && (
        <div className="dash-notif-counts">
          <span data-testid="notif-tile-counts" className="dash-text">
            {countLabel} · {counts.unread} unread
          </span>
          {onNavigate && <Button variant="secondary" size="sm" onClick={() => onNavigate('Notifications')}>Open notifications</Button>}
        </div>
      )}

      {/* The card stays visible when empty: deliberately not hideWhenEmpty. */}
      {nothingWaiting ? (
        <p className="dash-text">Nothing needs your attention right now.</p>
      ) : listed > 0 && !collapsed && (
        <div className="dash-notifs">
          {notifications.map(n => {
            const card = toNotificationCard(n);
            return (
              <NotificationCard
                key={card.id}
                card={card}
                onDismiss={onDismiss}
                onNavigate={onNavigate}
                onRead={onRead}
                isPersonal={isPersonal}
              />
            );
          })}
        </div>
      )}
    </DashboardCard>
  );
}
